import type { RowData } from "@tanstack/react-table";
import { AvatarGroup } from "@design-system/Avatar/AvatarGroup";
import type { DataGridPeopleMember } from "./cellValueTypes";
import { EmptyCellValue } from "./EmptyCellValue";
import { isPeopleValue, toPeopleLabel } from "./listDisplayValues";
import { PersonAvatar } from "./PersonCell";
import type { DataGridCellProps } from "./types";
import content from "./cellContent.module.scss";
import styles from "./cells.module.scss";

interface PeopleItem {
    member: DataGridPeopleMember;
    /** The member's position; names can repeat. */
    key: number;
}

const getKey = (item: PeopleItem) => item.key;
const getLabel = (item: PeopleItem) => toPeopleLabel(item.member);

/** One stacked avatar, its label (or dimmed reason) served by the cell tooltip host. */
const renderAvatar = ({ member }: PeopleItem) => {
    const label = toPeopleLabel(member);
    return (
        <span
            className={styles.personAvatar}
            data-dimmed={member.isDimmed ? "" : undefined}
            data-cell-tooltip={member.tooltip ?? label}
            data-cell-tooltip-always=""
        >
            <PersonAvatar person={member} name={label} isDecorative={false} />
        </span>
    );
};

interface PeopleCellProps<
    TData extends RowData,
> extends DataGridCellProps<TData> {
    max: number;
}

/**
 * People column cell: an `AvatarGroup` of `max` avatars, each named
 * "Name (Preparer, Reviewer)", and a +N chip listing the rest.
 */
export const PeopleCell = <TData extends RowData>({
    context,
    meta,
    max,
}: Readonly<PeopleCellProps<TData>>) => {
    const value = context.getValue();
    if (!isPeopleValue(value) || value.length === 0) {
        return <EmptyCellValue empty={meta.empty} />;
    }
    return (
        <span className={content.leading}>
            <AvatarGroup
                items={value.map((member, key) => ({ member, key }))}
                getKey={getKey}
                getLabel={getLabel}
                renderAvatar={renderAvatar}
                max={max}
            />
        </span>
    );
};
